import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence } from 'framer-motion'
import { db } from '@/src/renderer/lib/db/indexeddb'
import { scheduleSync } from '@/src/renderer/lib/sync/sync-engine'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { Button } from '@/src/renderer/components/ui/Button'
import { useIsActiveTab } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import { useAuth } from '@/src/renderer/hooks/useAuth'
import { useUserDisplayProfile } from '@/src/renderer/hooks/useUserDisplayProfile'
import { trackEvent } from '@/src/renderer/lib/analytics'
import { ANALYTICS_EVENTS } from '@/src/renderer/lib/analytics/events'
import avatarDefault from '@/src/renderer/assets/images/Sidebar/avatar-default.png'
import type { Goal, LifeSpace } from '@/src/renderer/lib/db/models'
import { useGoals } from '../../hooks/useGoals'
import { GOALS_CONTENT } from '../../lib/goalsContent'
import { GoalCard, type GoalSubmitData } from '../../components/GoalCard'
import './GoalsView.scss'

interface GoalsViewProps {
  lifeSpace: LifeSpace
}

/** Which goal (if any) is currently expanded into the centered editor. */
type Expanded =
  | { kind: 'edit'; goalId: string }
  | { kind: 'create' }
  | null

/**
 * The "Ziele" page for one life space.
 *
 * Clicking a goal card morphs it into a centered editor by animating geometry
 * (width / height / position) out of the card's box — never a transform-scale,
 * so nothing distorts. The grid keeps an invisible placeholder in the slot
 * (incl. while the editor animates back on close). Creating a goal grows the
 * editor out of the CTA button.
 */
export const GoalsView: React.FC<GoalsViewProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const goals = useGoals(lifeSpace)
  const [expanded, setExpanded] = useState<Expanded>(null)
  // Goal whose grid slot must stay a placeholder while the editor animates
  // back into it (covers the full close/exit animation).
  const [exitingGoalId, setExitingGoalId] = useState<string | null>(null)
  // Keeps the overlay layer mounted through the editor's exit animation.
  const [isClosing, setIsClosing] = useState(false)
  const assets = GOALS_CONTENT[lifeSpace]
  const isActive = useIsActiveTab()

  const rootRef = useRef<HTMLDivElement>(null)
  const createBtnRef = useRef<HTMLButtonElement>(null)
  // Box the editor grows out of / shrinks back into, and the centering layer's
  // box — both captured at open time so the morph geometry is stable.
  const sourceRect = useRef<DOMRect | null>(null)
  const overlayRect = useRef<DOMRect | null>(null)
  // Mirror of `expanded` so the global "A" key listener can read the current
  // state without re-subscribing on every open/close.
  const expandedRef = useRef(expanded)
  expandedRef.current = expanded

  // Author shown on every goal card — resolved once, passed down.
  const { user } = useAuth()
  const { avatarUrl, fullName, isLoading: authorLoading } = useUserDisplayProfile(
    user?.id ?? 'offline-user',
    24,
  )
  // Until the profile resolves, cards render a skeleton instead of the default
  // "Nutzer" + avatar — otherwise the real author visibly flashes in over them.
  const authorReady = !authorLoading
  const authorName = fullName || t('userMenu.defaultName')
  const authorAvatarUrl = avatarUrl || avatarDefault

  const goalsById = useMemo(() => {
    const map = new Map<string, Goal>()
    goals?.forEach((g) => map.set(g.id, g))
    return map
  }, [goals])

  const openEdit = useCallback((goalId: string, rect: DOMRect) => {
    sourceRect.current = rect
    overlayRect.current = rootRef.current?.getBoundingClientRect() ?? null
    setExitingGoalId(null)
    setExpanded({ kind: 'edit', goalId })
  }, [])

  const openCreate = useCallback(() => {
    sourceRect.current = createBtnRef.current?.getBoundingClientRect() ?? null
    overlayRect.current = rootRef.current?.getBoundingClientRect() ?? null
    setExpanded({ kind: 'create' })
  }, [])

  const close = useCallback(() => {
    if (!expanded) return
    if (expanded.kind === 'edit') setExitingGoalId(expanded.goalId)
    setIsClosing(true)
    setExpanded(null)
  }, [expanded])

  // "A" opens the create editor. Gated on the active tab so an inactive
  // (display:none) Ziele tab does not steal the keypress — CLAUDE.md gotcha.
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'a' && e.key !== 'A') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return
      if (expandedRef.current) return
      e.preventDefault()
      openCreate()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, openCreate])

  // ESC closes the expanded editor.
  useEffect(() => {
    if (!expanded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [expanded, close])

  // Takes a goalId (not the goal object) so the grid can pass this stable
  // callback to every memoized card without recreating it per render.
  const handleDelete = useCallback(
    async (goalId: string) => {
      try {
        await db.deleteGoal(goalId)
        scheduleSync()
      } catch (err) {
        console.error('[Goals] delete failed:', err)
        return
      }
      showUndoToast({
        message: t('goals.toast.deleted'),
        undoLabel: t('common.undo'),
        onUndo: async () => {
          try {
            await db.updateGoal(goalId, { deletedAt: null })
            scheduleSync()
          } catch (err) {
            console.error('[Goals] undo delete failed:', err)
          }
        },
      })
    },
    [t],
  )

  const handleSubmit = useCallback(
    async (data: GoalSubmitData) => {
      if (!expanded) return
      try {
        if (expanded.kind === 'create') {
          await db.createGoal({
            title: data.title,
            description: data.description,
            lifeSpace,
          })
          trackEvent(ANALYTICS_EVENTS.GOAL_CREATED, { life_space: lifeSpace })
          scheduleSync()
          // Editor shrinks back toward the CTA button; the new card appears in
          // the grid as the page re-renders from the live query.
          setIsClosing(true)
          setExpanded(null)
        } else {
          await db.updateGoal(expanded.goalId, {
            title: data.title,
            description: data.description,
          })
          scheduleSync()
          close()
        }
      } catch (err) {
        // Keep the editor open so the user's input isn't lost on a write error.
        console.error('[Goals] save failed:', err)
      }
    },
    [expanded, lifeSpace, close],
  )

  if (goals === undefined) {
    return <div className="goals-view" aria-busy="true" aria-label={t('goals.loading')} />
  }

  const isEmpty = goals.length === 0
  const editGoal = expanded?.kind === 'edit' ? goalsById.get(expanded.goalId) : undefined

  return (
    <div
      ref={rootRef}
      className={`goals-view ${expanded || isClosing ? 'goals-view--expanded' : ''} ${expanded ? 'goals-view--dim' : ''}`}
    >
      <div className="goals-view__scroll">
        <div className="goals-view__inner">
          <header className="goals-view__header">
            <img className="goals-view__icon" src={assets.icon} alt="" aria-hidden />
            <div className="goals-view__header-content">
              <div className="goals-view__header-text">
                <h1 className="goals-view__title">{t(`goals.page.${lifeSpace}.title`)}</h1>
                <p className="goals-view__description">
                  {t(`goals.page.${lifeSpace}.description`)}
                </p>
              </div>
              <Button
                ref={createBtnRef}
                className="goals-view__cta"
                variant="primary"
                shortcut={{ variant: 'character', text: 'A' }}
                onClick={openCreate}
              >
                {t(isEmpty ? 'goals.cta.empty' : 'goals.cta.more')}
              </Button>
            </div>
          </header>

          {!isEmpty && (
            <div className="goals-view__grid">
              {goals.map((goal) => {
                // Hold the slot with an inert placeholder while this goal is
                // expanded — and while the editor animates back on close.
                const isHeldOpen = expanded?.kind === 'edit' && expanded.goalId === goal.id
                const isHeldClosing = exitingGoalId === goal.id
                return isHeldOpen || isHeldClosing ? (
                  <GoalCard
                    key={goal.id}
                    mode="collapsed"
                    placeholder
                    goal={goal}
                    authorName={authorName}
                    authorAvatarUrl={authorAvatarUrl}
                    authorReady={authorReady}
                  />
                ) : (
                  <GoalCard
                    key={goal.id}
                    mode="collapsed"
                    goal={goal}
                    authorName={authorName}
                    authorAvatarUrl={authorAvatarUrl}
                    authorReady={authorReady}
                    onOpen={openEdit}
                    onDelete={handleDelete}
                  />
                )
              })}
            </div>
          )}
        </div>

        {/* Background illustration — empty state only (Grilling #10). */}
        {isEmpty && (
          <img className="goals-view__hero" src={assets.hero} alt="" aria-hidden />
        )}
      </div>

      {/* Transparent layer — catches outside clicks, no dimming. Stays mounted
          through the editor's exit animation (isClosing). */}
      {(expanded || isClosing) && (
        <div className="goals-view__overlay-layer" onClick={close}>
          <AnimatePresence
            onExitComplete={() => {
              setIsClosing(false)
              setExitingGoalId(null)
              sourceRect.current = null
            }}
          >
            {expanded && (
              <GoalCard
                key="goal-editor"
                mode="expanded"
                expandedKind={expanded.kind}
                goal={editGoal}
                sourceRect={sourceRect.current}
                overlayRect={overlayRect.current}
                authorName={authorName}
                authorAvatarUrl={authorAvatarUrl}
                authorReady={authorReady}
                onSubmit={handleSubmit}
                onClose={close}
              />
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
